import { Injectable } from '@angular/core';
import { Badge } from '../models/badge.model';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class BadgesService {
  private readonly basePath = '/badges';

  constructor(private apiService: ApiService) {}

  getCatalog() {
    return this.apiService.request(`${this.basePath}/catalog`, 'GET');
  }

  getUserBadgesByUserId(userId: string) {
    return this.apiService.request(`${this.basePath}/user/${userId}`, 'GET');
  }

  getRanking() {
    return this.apiService.request(`${this.basePath}/ranking`, 'GET');
  }

  normalizeRankingResponse(response: any): any[] {
    const entries = Array.isArray(response)
      ? response
      : Array.isArray(response?.ranking)
        ? response.ranking
        : [];

    return entries.map((entry: any) => {
      const badges: Badge[] = (entry.unlockedBadges ?? []).map((ub: any) =>
        this.normalizeBadge(ub, true),
      );

      return {
        user: entry.user,
        badges,
        gold: entry.badgesByTier?.gold ?? 0,
        silver: entry.badgesByTier?.silver ?? 0,
        bronze: entry.badgesByTier?.bronze ?? 0,
        total: entry.totalBadges ?? badges.length,
      };
    });
  }

  normalizeCatalogResponse(response: any): Badge[] {
    const catalog = Array.isArray(response)
      ? response
      : Array.isArray(response?.catalog)
        ? response.catalog
        : Array.isArray(response?.badges)
          ? response.badges
          : [];

    return catalog
      .map((badge: any) => this.normalizeBadge(badge, false))
      .filter((badge: Badge): badge is Badge => !!badge.id);
  }

  normalizeUserBadgesResponse(response: any, catalog: Badge[] = []): Badge[] {
    const resolvedCatalog =
      catalog.length > 0 ? catalog : this.normalizeCatalogResponse(response);
    const responseBadges = Array.isArray(response)
      ? response
      : Array.isArray(response?.badges)
        ? response.badges
        : [];

    if (
      responseBadges.length > 0 &&
      this.looksLikeBadgeObjects(responseBadges)
    ) {
      return responseBadges.map((badge: any) =>
        this.normalizeBadge(badge, badge?.unlocked !== false),
      );
    }

    const unlockedIds = this.extractUnlockedIds(response);
    const progress = response?.progress ?? {};

    return resolvedCatalog.map((badge) => ({
      ...badge,
      unlocked: unlockedIds.includes(String(badge.id)),
      progress: progress[badge.id] ?? undefined,
    }));
  }

  private looksLikeBadgeObjects(badges: any[]): boolean {
    return badges.some(
      (badge) =>
        badge &&
        typeof badge === 'object' &&
        ('id' in badge ||
          '_id' in badge ||
          'title' in badge ||
          'tier' in badge ||
          'badge' in badge),
    );
  }

  private extractUnlockedIds(response: any): string[] {
    const rawIds = Array.isArray(response?.badgeIds)
      ? response.badgeIds
      : Array.isArray(response?.badges)
        ? response.badges
        : Array.isArray(response?.unlockedIds)
          ? response.unlockedIds
        : Array.isArray(response?.unlockedBadgeIds)
          ? response.unlockedBadgeIds
          : [];

    return rawIds
      .map((badge: any) => this.extractBadgeId(badge))
      .filter((id: string | null): id is string => !!id);
  }

  private extractBadgeId(badge: any): string | null {
    if (!badge) {
      return null;
    }

    if (typeof badge === 'string' || typeof badge === 'number') {
      return String(badge);
    }

    const source = badge.badge ?? badge;
    const id = source?.id ?? source?._id ?? badge?.badgeId ?? badge?.badge?._id;

    return id ? String(id) : null;
  }

  private normalizeBadge(badge: any, unlocked: boolean): Badge {
    const source = badge?.badge ?? badge;

    return {
      id: this.extractBadgeId(badge) || '',
      title: source?.title ?? source?.name ?? '',
      description: source?.description ?? source?.desc ?? '',
      icon: source?.icon ?? source?.emoji ?? '🏅',
      tier: source?.tier ?? 'bronze',
      unlocked: badge?.unlocked ?? unlocked,
      progress: badge?.progress,
    };
  }
}
